/**
 * Implementation primitives for the per-platform **obscuration** polygon
 * layer (PR5b inc4; REQ-UI-070; ADR-032 §4.2) — a thin per-kind binding over
 * the shared geometry-polygon mechanism in :file:`geometry-polygon-layer.ts`.
 * Sibling of :file:`fov-layer-impl.ts` / :file:`coverage-layer-impl.ts`;
 * split from :file:`obscuration-layer.tsx` per ``react-refresh/only-export-components``.
 *
 * Like coverage (and unlike FoV), a platform's obscuration is a **list** of
 * rings (:type:`StatusGeometry.obscuration` — ``number[][][]``), so the ring
 * extractor returns that list directly (``g => g.obscuration``) and a single
 * platform contributes **one ``Polygon`` feature per ring** (each tagged with
 * its ``ring_index``). The shared mechanism then renders ALL platforms'
 * obscuration rings in one source as a faint violet fill (``--geom-
 * obscuration`` at low opacity) plus a more-saturated outline (``--geom-
 * obscuration-stroke``) so the masked region stays operator-visible once the
 * fill drops to a wash (CLAUDE.md §8.1). Both paints resolve through the inc1
 * design-system readers so the theme flip carries through on the next paint
 * (REQ-UI-040).
 *
 * The fill + line layers are inserted **below** the snapshot marker layer
 * (handled by the shared :func:`attachGeometryPolygonLayer`) so the platform
 * markers always paint on top of the geometry wash (per §8.1).
 *
 * Cross-references:
 *   - REQ-UI-070 (SAPIENT map geometry — obscuration layer) / REQ-UI-040 /
 *     REQ-UI-046.
 *   - ADR-032 §4.2 (Status geometry data model — obscuration ring list).
 *   - CLAUDE.md §4.4 (no duplicated logic) / §8.1 (markers above the wash).
 *   - map/geometry-polygon-layer.ts (the shared mechanism this delegates to).
 *   - map/marker-styling.ts (getGeomColor / getGeomStrokeColor).
 */
import type { Map as MapLibreMap } from "maplibre-gl";
import { getGeomColor, getGeomStrokeColor } from "@/map/marker-styling";
import type { StatusGeometry } from "@/map/sse-types";
import {
  type GeometryEntry,
  type GeometryFeatureProps,
  type RingExtractor,
  attachGeometryPolygonLayer,
  detachGeometryPolygonLayer,
  ringsToPolygonFeatures,
} from "@/map/geometry-polygon-layer";

/** MapLibre GeoJSON source ID for the obscuration polygons. */
export const OBSCURATION_SOURCE_ID = "meridian-obscuration-source";
/** MapLibre fill-layer ID for the obscuration wash. */
export const OBSCURATION_FILL_LAYER_ID = "meridian-obscuration-fill-layer";
/** MapLibre line-layer ID for the obscuration outline. */
export const OBSCURATION_LINE_LAYER_ID = "meridian-obscuration-line-layer";

/**
 * Fill opacity for the obscuration wash — faint so the basemap + markers
 * (and the coverage / FoV washes that may stack with it) read through it;
 * the saturated outline carries the shape. Within the ~0.12–0.2 band
 * documented on :func:`getGeomColor`.
 */
export const OBSCURATION_FILL_OPACITY = 0.15;
/** Outline width in screen px — thin but legible against the wash. */
export const OBSCURATION_LINE_WIDTH_PX = 1.5;

/**
 * A platform's obscuration entry as the feature builder consumes it. Alias
 * of the shared :type:`GeometryEntry`.
 */
export type ObscurationEntry = GeometryEntry;

/**
 * Pull the obscuration ring **list** out of a platform's geometry. Returns
 * ``geometry.obscuration`` directly (``number[][][]``); the shared builder
 * emits one feature per drawable ring (skipping null / < 3-point rings) and
 * tags each with its ``ring_index``.
 */
const extractObscurationRings: RingExtractor = (geometry: StatusGeometry) => geometry.obscuration;

/**
 * Build the GeoJSON source payload — one closed, antimeridian-unwrapped
 * ``Polygon`` feature **per obscuration ring** across all platforms (a
 * platform with two obscuration rings yields two features, ``ring_index`` 0
 * and 1). Delegates to the shared :func:`ringsToPolygonFeatures` with the
 * obscuration ring extractor (CLAUDE.md §4.4); null / too-short rings are
 * skipped.
 *
 * Properties carry ``producer_id`` + ``ring_index``. Exported for unit
 * testability.
 */
export function obscurationToFeatureCollection(
  entries: readonly ObscurationEntry[],
): GeoJSON.FeatureCollection<GeoJSON.Polygon, GeometryFeatureProps> {
  return ringsToPolygonFeatures(entries, extractObscurationRings);
}

/**
 * Register the obscuration source + fill layer + line layer (idempotent:
 * later calls ``setData`` + re-apply the theme-resolved paint colours),
 * painted **below** the snapshot marker layer (CLAUDE.md §8.1). Delegates to
 * the shared :func:`attachGeometryPolygonLayer` with the obscuration IDs +
 * paints + theme-resolved colours.
 *
 * Exported for unit testability. The :class:`ObscurationLayer` component (in
 * :file:`obscuration-layer.tsx`) owns the React-effect lifecycle + the
 * visibility/zoom gate (via the shared :func:`useGeometryLayerEffect`).
 */
export function attachObscurationLayer(
  map: MapLibreMap,
  entries: readonly ObscurationEntry[],
): void {
  attachGeometryPolygonLayer(map, {
    sourceId: OBSCURATION_SOURCE_ID,
    fillLayerId: OBSCURATION_FILL_LAYER_ID,
    lineLayerId: OBSCURATION_LINE_LAYER_ID,
    features: obscurationToFeatureCollection(entries),
    fillColor: getGeomColor("obscuration"),
    strokeColor: getGeomStrokeColor("obscuration"),
    fillOpacity: OBSCURATION_FILL_OPACITY,
    lineWidth: OBSCURATION_LINE_WIDTH_PX,
  });
}

/**
 * Tear down the obscuration fill + line layers and source. Delegates to the
 * shared :func:`detachGeometryPolygonLayer`. No-op when nothing is
 * registered.
 */
export function detachObscurationLayer(map: MapLibreMap): void {
  detachGeometryPolygonLayer(map, {
    sourceId: OBSCURATION_SOURCE_ID,
    fillLayerId: OBSCURATION_FILL_LAYER_ID,
    lineLayerId: OBSCURATION_LINE_LAYER_ID,
  });
}
